import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

/*
 * The KPI card, and the reason it is a component with rules rather than a div.
 *
 * **It cannot render an em-dash for a number.** That is defects P1 and P10.
 * The prototype's Reports page shows `—` in all four cards permanently, because
 * nothing ever fetches the stats — the cards are decoration. Its client detail
 * page shows `Customer since —` for the same reason.
 *
 * Three states, and they are different things:
 *
 *   - **Loading** is a skeleton. Not a dash, not a zero.
 *   - **Zero** is `0`. A business with no jobs today has zero jobs today, and
 *     that is a fact worth showing.
 *   - **Unknown** is a sentence saying why, in the client portal's voice —
 *     "Not yet recorded", not a punctuation mark.
 *
 * The type enforces it: `value` is a number, so there is no way to pass a dash.
 * Absence is `value={null}` plus an `unknownLabel` that has to say something.
 */

export interface KpiCardProps {
  label: string;
  /**
   * The figure. `null` means genuinely not known — and then `unknownLabel`
   * explains it. Zero is a number and renders as `0`.
   */
  value: number | null;
  /** Formats the number for display — currency, hours, a percentage. */
  format?: (value: number) => string;
  /** Shown instead of the figure when `value` is null. Must say *why*. */
  unknownLabel?: string;
  /** Under the figure. "vs. last month", "across 4 properties". */
  subLabel?: ReactNode;
  icon?: LucideIcon;
  isLoading?: boolean;
  className?: string;
}

export function KpiCard({
  label,
  value,
  format,
  unknownLabel = 'Not yet recorded',
  subLabel,
  icon: Icon,
  isLoading = false,
  className,
}: KpiCardProps) {
  return (
    <div
      className={cn('rounded-lg border border-ink-300 bg-white p-4 shadow-card', className)}
      // The figure replaces a skeleton without the page moving, so a screen
      // reader is told when it arrives rather than being left on the skeleton.
      aria-busy={isLoading || undefined}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-body text-ink-500">{label}</p>
        {Icon ? (
          <span className="grid size-8 shrink-0 place-items-center rounded-full bg-honey-50">
            <Icon aria-hidden="true" className="size-5 text-bark-700" />
          </span>
        ) : null}
      </div>

      <div className="mt-2">
        {isLoading ? (
          <>
            <span className="sr-only">Loading {label}</span>
            <div aria-hidden="true" className="h-[36px] w-24 animate-pulse rounded-sm bg-ink-100" />
          </>
        ) : value === null ? (
          // Italic, muted, and a sentence — the client portal's pattern, which
          // design-system.md §7 says wins when admin and client disagree.
          <p className="py-1.5 text-body italic text-ink-500">{unknownLabel}</p>
        ) : (
          <p className="text-kpi text-ink-900">{format ? format(value) : value.toLocaleString()}</p>
        )}
      </div>

      {subLabel ? <p className="mt-1 text-meta text-ink-500">{subLabel}</p> : null}
    </div>
  );
}
